import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { SectionLabel } from './DashboardWrapper';
import {
  hasCompetitiveIntel,
  getDomainStrength,
  getMonthlyOrganicValue,
  getTotalOrganicKeywords,
  formatCurrency,
  formatNumber as formatNum,
  getDomainStrengthColor,
  getTrafficValue,
  sortBusinessesBySeoScore,
} from '../../utils/competitiveIntelHelpers';
import { useUser } from '../../contexts/UserContext';
import BusinessReviewModal from '../BusinessReviewModal';
import { getYearData } from '../../services/yearService';
import { logger } from '../../utils/logger';
import {
  Box,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  Heading,
  Text,
  Flex,
  Badge,
  Icon,
  useColorModeValue,
  HStack,
  VStack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Progress,
  Link,
  Avatar,
  AvatarGroup,
  Divider,
  Button,
  Tooltip,
  Spinner,
  Grid,
  Card,
  CardBody,
} from '@chakra-ui/react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  CartesianGrid,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ScatterChart,
  Scatter,
  ReferenceLine,
  AreaChart,
  Area,
} from 'recharts';
import ModernDetailsTable from '../ModernDetailsTable';
import { 
  FaGlobe, 
  FaChartLine, 
  FaUsers, 
  FaSearch, 
  FaStar, 
  FaMapMarkerAlt, 
  FaShareAlt, 
  FaFacebook, 
  FaInstagram, 
  FaTwitter, 
  FaLink, 
  FaTrophy, 
  FaSort, 
  FaFilter,
  FaExternalLinkAlt,
  FaArrowUp,
  FaFireAlt,
  FaChartBar,
  FaListOl,
  FaBullseye,
  FaLightbulb,
} from 'react-icons/fa';
import { InfoOutlineIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import { createFavoriteStarPieLabelRenderer } from '../FavoriteStarIndicator';
import MetricCard from '../MetricCard';
import BusinessBubbleChart from '../BusinessBubbleChart';

const COLORS = [
  '#2F80ED', // bright blue
  '#27AE60', // emerald green
  '#EB5757', // bright red
  '#9B51E0', // purple
  '#F2994A', // orange
  '#219EBC', // teal
  '#F264A6', // pink
  '#BB6BD9', // violet
  '#2D9CDB', // light blue
  '#219653', // dark green
  '#F2C94C', // yellow
  '#6FCF97', // light green
  '#56CCF2', // sky blue
  '#F2994A', // peach
  '#9B51E0', // lavender
  '#EB5757', // coral
  '#2F80ED', // royal blue
  '#27AE60', // forest green
  '#BB6BD9', // orchid
  '#F264A6'  // rose
];

const WebTrafficDashboard = ({ reviewsData = [], demoMode, demoData }) => {
  // Set up color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const statBg = useColorModeValue('gray.50', 'gray.700');
  const searchBg = useColorModeValue('white', 'gray.700');
  const searchBorder = useColorModeValue('gray.200', 'gray.600');
  const topRankingHoverBg = useColorModeValue('gray.100', 'gray.700');
  
  // Get favorite URIs from user context
  const { favoriteUris } = useUser();
  
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [detailSearchQuery, setDetailSearchQuery] = useState('');
  const [yearData, setYearData] = useState([]);
  const [combinedData, setCombinedData] = useState([]);
  
  // Additional color values for methodology section
  const methodologyHeadingColor = useColorModeValue('blue.600', 'blue.300');
  const methodologyTextColor = useColorModeValue('gray.600', 'gray.400');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const subtitleColor = useColorModeValue('gray.500', 'gray.400');

  // Fetch year data for BusinessReviewModal
  useEffect(() => {
    if (demoMode && demoData) {
      setYearData(demoData.yearData || []);
      setCombinedData(demoData.combinedData || []);
      return;
    }
    const fetchYearData = async () => {
      try {
        const yearDataResponse = await getYearData();
        setYearData(yearDataResponse);

        // Combine reviewsData with yearData for the modal
        // NOTE: Do NOT spread historicalData?.data directly - it can overwrite
        // important fields like traffic_data and query_ranking_info.
        // Keep historicalData as a nested property, matching the pattern used
        // by GrossGrowthDashboard and TotalReviewsDashboard.
        const combined = reviewsData.map((currentData) => {
          const historicalData = yearDataResponse.find(
            (yd) => yd.maps_uri === currentData.maps_uri
          );
          return {
            ...currentData,
            historicalData: historicalData?.data || null,
          };
        });
        setCombinedData(combined);
      } catch (error) {
        logger.error('Error fetching year data for WebTrafficDashboard:', error);
      }
    };

    if (reviewsData && reviewsData.length > 0) {
      fetchYearData();
    }
  }, [reviewsData]);

  // Sorted arrays for BusinessReviewModal
  const sortedByTotal = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData].sort((a, b) => (b.rating_count || 0) - (a.rating_count || 0));
  }, [combinedData]);

  // Compute sentiment scores from historicalData (matching GrossGrowthDashboard pattern)
  const businessSentimentData = useMemo(() => {
    if (!combinedData.length) return [];
    return combinedData.map((business) => {
      const sentimentBreakdown = business.historicalData?.sentimentBreakdown || {
        positive: 0,
        neutral: 0,
        negative: 0,
      };
      const total =
        sentimentBreakdown.positive +
        sentimentBreakdown.neutral +
        sentimentBreakdown.negative;
      const sentimentScore =
        total > 0
          ? ((sentimentBreakdown.positive + sentimentBreakdown.neutral * 0.5) /
              total) *
            100
          : 0;
      return { ...business, sentimentScore, ...sentimentBreakdown, total };
    });
  }, [combinedData]);

  const sortedBySentiment = useMemo(() => {
    if (!businessSentimentData.length) return [];
    return [...businessSentimentData].sort(
      (a, b) => b.sentimentScore - a.sentimentScore
    );
  }, [businessSentimentData]);

  // Compute growth metrics from historicalData (matching GrossGrowthDashboard pattern)
  const businessGrowthData = useMemo(() => {
    if (!combinedData || combinedData.length === 0) return [];
    return combinedData.map((business) => {
      const monthlyData = business.historicalData?.monthlyReviewCounts || {};
      const months = Object.keys(monthlyData).sort();
      if (months.length === 0) {
        return { ...business, totalGrowth: 0 };
      }
      const totalPeriodGrowth = months.reduce(
        (sum, month) => sum + (monthlyData[month] || 0),
        0
      );
      return { ...business, totalGrowth: totalPeriodGrowth };
    });
  }, [combinedData]);

  const sortedByGrowth = useMemo(() => {
    if (!businessGrowthData.length) return [];
    return [...businessGrowthData].sort(
      (a, b) => (b.totalGrowth || 0) - (a.totalGrowth || 0)
    );
  }, [businessGrowthData]);

  const sortedByTrafficForModal = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData]
      .map((business) => ({
        ...business,
        trafficValue: getTrafficValue(business),
      }))
      .filter((business) => (business.trafficValue || 0) > 0)
      .sort((a, b) => (b.trafficValue || 0) - (a.trafficValue || 0));
  }, [combinedData]);

  const sortedBySeo = useMemo(
    () => sortBusinessesBySeoScore(combinedData),
    [combinedData]
  );

  // Create a map from shortened business names to their maps_uri for favorites check
  const businessNameToUriMap = useMemo(() => {
    const map = {};
    if (reviewsData) {
      reviewsData.forEach(business => {
        const shortName = business.name?.split(' ').slice(0, 2).join(' ');
        if (shortName && business.maps_uri) {
          map[shortName] = business.maps_uri;
        }
      });
    }
    return map;
  }, [reviewsData]);

  // Custom label renderer for bar charts that shows spinning stars for favorited businesses
  const FavoriteStarBarLabel = useCallback((props) => {
    const { x, y, width, payload } = props;
    const datumName = payload?.name ?? props?.name;
    
    // Check if this business is favorited
    const mapsUri = payload?.maps_uri || payload?.mapsUri || businessNameToUriMap[datumName];
    const isFavorited = mapsUri && favoriteUris?.includes(mapsUri);
    
    if (!isFavorited) return null;
    
    const starSize = 18;
    const starX = x + (width / 2);
    // Keep the star within the visible SVG area (top bars can otherwise clip it)
    const starY = Math.max(y - starSize - 4, 2);
    
    return (
      <g transform={`translate(${starX - starSize/2}, ${starY})`}>
        <g>
          <animateTransform
            attributeName="transform"
            attributeType="XML"
            type="rotate"
            from={`0 ${starSize/2} ${starSize/2}`}
            to={`360 ${starSize/2} ${starSize/2}`}
            dur="3s"
            repeatCount="indefinite"
          />
          <path
            d={`M${starSize/2 + 1},${2} L${starSize*0.63 + 1},${starSize*0.38 + 2} L${starSize + 1},${starSize*0.38 + 2} L${starSize*0.69 + 1},${starSize*0.62 + 2} L${starSize*0.81 + 1},${starSize + 2} L${starSize/2 + 1},${starSize*0.75 + 2} L${starSize*0.19 + 1},${starSize + 2} L${starSize*0.31 + 1},${starSize*0.62 + 2} L${1},${starSize*0.38 + 2} L${starSize*0.37 + 1},${starSize*0.38 + 2} Z`}
            fill="#8B7355"
            fillOpacity="0.3"
          />
          <path
            d={`M${starSize/2},0 L${starSize*0.63},${starSize*0.38} L${starSize},${starSize*0.38} L${starSize*0.69},${starSize*0.62} L${starSize*0.81},${starSize} L${starSize/2},${starSize*0.75} L${starSize*0.19},${starSize} L${starSize*0.31},${starSize*0.62} L0,${starSize*0.38} L${starSize*0.37},${starSize*0.38} Z`}
            fill="#D4AF37"
            stroke="#B8860B"
            strokeWidth="1"
          />
          <path
            d={`M${starSize/2},0 L${starSize*0.63},${starSize*0.38} L${starSize},${starSize*0.38} L${starSize*0.69},${starSize*0.62} L${starSize*0.81},${starSize} L${starSize/2},${starSize*0.75} L${starSize*0.19},${starSize} L${starSize*0.31},${starSize*0.62} L0,${starSize*0.38} L${starSize*0.37},${starSize*0.38} Z`}
            fill="#FFD700"
            fillOpacity="0.7"
          />
          <ellipse
            cx={starSize/2}
            cy={starSize*0.3}
            rx={starSize*0.18}
            ry={starSize*0.14}
            fill="#FFFACD"
            fillOpacity="0.8"
          />
        </g>
      </g>
    );
  }, [businessNameToUriMap, favoriteUris]);

  // Custom label renderer for pie charts that shows spinning stars for favorited businesses
  const FavoriteStarPieLabel = useMemo(() => {
    return createFavoriteStarPieLabelRenderer({
      businessNameToUriMap,
      favoriteUris
    });
  }, [businessNameToUriMap, favoriteUris]);

  // Helper function to calculate SEO Score
  const calculateSeoScore = (business) => {
    const visitors = getTrafficValue(business) || 0;
    if (visitors === 0) return 0;

    let rankForSeo = business.query_ranking_info?.url_ranking;

    if (typeof rankForSeo === 'number' && rankForSeo > 0 && rankForSeo <= 100) {
      return visitors / rankForSeo;
    } else {
      // Unranked, ranked > 100, or invalid rank: use 100 as divisor
      return visitors / 100;
    }
  };

  // Calculate metrics
  const businessesWithTraffic = useMemo(() => {
    return reviewsData
      .map(item => ({ ...item, trafficValue: getTrafficValue(item) || 0 }))
      .filter(item => item.trafficValue > 0 && item.name !== "Honky Tonk Highway")
      .sort((a, b) => b.trafficValue - a.trafficValue);
  }, [reviewsData]);

  // Filter businesses
  const filteredBusinesses = useMemo(() => {
    if (!businessesWithTraffic.length) return [];
    return businessesWithTraffic;
  }, [businessesWithTraffic]);

  // Sort businesses by traffic
  const sortedBusinesses = useMemo(() => {
    if (!filteredBusinesses.length) return [];

    return [...filteredBusinesses].sort((a, b) => {
      return (b.trafficValue || 0) - (a.trafficValue || 0);
    });
  }, [filteredBusinesses]);

  const totalMonthlyVisitors = useMemo(() => {
    return businessesWithTraffic.reduce((sum, item) =>
      sum + (item.trafficValue || 0), 0
    );
  }, [businessesWithTraffic]);

  const avgMonthlyVisitors = useMemo(() => {
    return totalMonthlyVisitors / businessesWithTraffic.length;
  }, [totalMonthlyVisitors, businessesWithTraffic]);

  const medianMonthlyVisitors = useMemo(() => {
    const sorted = [...businessesWithTraffic]
      .map(item => item.trafficValue || 0)
      .sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0 
      ? (sorted[mid - 1] + sorted[mid]) / 2 
      : sorted[mid];
  }, [businessesWithTraffic]);

  const top5Traffic = useMemo(() => {
    return sortedBusinesses.slice(0, 5).map(business => {
      const rank = business.query_ranking_info?.url_ranking;
      const traffic = business.trafficValue;
      let estimatedSeoScore = null;
      if (rank && typeof rank === 'number' && rank > 0 && traffic && typeof traffic === 'number') {
        estimatedSeoScore = traffic / rank;
      }
      return {
        ...business,
        estimatedSeoScore
      };
    });
  }, [sortedBusinesses]);

  const bottom5Traffic = useMemo(() =>
    [...sortedBusinesses]
      .sort((a, b) => (a.trafficValue || 0) - (b.trafficValue || 0))
      .slice(0, 5),
    [sortedBusinesses]
  );

  // Table columns configuration
  const columns = [
    {
      id: 1,
      name: 'Business Name',
      selector: row => row.name,
      sortable: true,
      minWidth: '200px',
      cell: row => (
        <HStack spacing={2}>
          <Text fontWeight="semibold" fontSize="md" noOfLines={1}>{row.name}</Text>
          {(getTrafficValue(row) || 0) > avgMonthlyVisitors && (
            <Badge colorScheme="green" size="sm" borderRadius="full">
              <Icon as={FaArrowUp} boxSize={2} />
            </Badge>
          )}
        </HStack>
      ),
    },
    {
      id: 2,
      name: 'Monthly Traffic',
      selector: row => getTrafficValue(row) || 0,
      sortable: true,
      right: true,
      cell: row => (
        <Text fontWeight="semibold" fontFeatureSettings="'tnum' 1">
          {(getTrafficValue(row) || 0).toLocaleString()}
        </Text>
      ),
    },
    {
      id: 3,
      name: 'Search Rank',
      selector: row => row.query_ranking_info?.url_ranking || 999,
      sortable: true,
      right: true,
      cell: row => {
        const rank = row.query_ranking_info?.url_ranking;
        if (!rank) {
          return (
            <Badge colorScheme="gray" borderRadius="full" px={2}>
              {'>100'}
            </Badge>
          );
        }
        const colorScheme = rank <= 10 ? 'green' : rank <= 20 ? 'blue' : rank <= 50 ? 'yellow' : 'gray';
        return (
          <Badge colorScheme={colorScheme} borderRadius="full" px={2}>
            #{rank}
          </Badge>
        );
      },
    },
    {
      id: 4,
      name: 'Social Media',
      selector: row => {
        const social = row.traffic_data?.metrics?.social || {};
        return Object.values(social).filter(Boolean).length;
      },
      sortable: true,
      right: true,
      cell: row => {
        const social = row.traffic_data?.metrics?.social || {};
        const count = Object.values(social).filter(Boolean).length;
        return (
          <HStack spacing={1}>
            <Icon as={FaFacebook} color={social.facebook ? "blue.500" : "gray.300"} boxSize={4} />
            <Icon as={FaInstagram} color={social.instagram ? "purple.500" : "gray.300"} boxSize={4} />
            <Icon as={FaTwitter} color={social.twitter ? "blue.400" : "gray.300"} boxSize={4} />
          </HStack>
        );
      },
    },
    {
      id: 5,
      name: 'Status',
      selector: row => row.traffic_data?.metrics?.page_info?.status_code || 0,
      sortable: true,
      right: true,
      cell: row => {
        const status = row.traffic_data?.metrics?.page_info?.status_code;
        return (
          <Badge 
            colorScheme={status === 200 ? "green" : "red"} 
            borderRadius="full"
            px={2}
          >
            {status || 'N/A'}
          </Badge>
        );
      },
    },
    {
      id: 6,
      name: 'Reviews',
      selector: row => row.rating_count || 0,
      sortable: true,
      right: true,
      cell: row => (
        <Text fontWeight="medium" fontFeatureSettings="'tnum' 1">
          {(row.rating_count || 0).toLocaleString()}
        </Text>
      ),
    },
    {
      id: 7,
      name: 'Competitive Intel',
      sortable: false,
      cell: row => {
        const hasCI = hasCompetitiveIntel(row);
        if (!hasCI) {
          return <Text fontSize="xs" color="gray.400">No data</Text>;
        }
        const domainStrength = getDomainStrength(row);
        const organicValue = getMonthlyOrganicValue(row);
        const strengthColor = getDomainStrengthColor(domainStrength);
        return (
          <VStack spacing={1} align="flex-end">
            <Badge colorScheme={strengthColor} fontSize="xs" variant="solid" borderRadius="full">
              {domainStrength}/100
            </Badge>
            {organicValue > 0 && (
              <Text fontSize="xs" color="purple.600" fontWeight="semibold">
                {formatCurrency(organicValue)}
              </Text>
            )}
          </VStack>
        );
      },
      width: '120px'
    }
  ];

  // Businesses sorted by SEO score for the Overview charts
  const sortedBusinessesBySeoScore = useMemo(() => {
    return reviewsData
      .filter(item => (getTrafficValue(item) || 0) > 0)
      .map(business => ({
        ...business,
        seoScore: calculateSeoScore(business),
      }))
      .sort((a, b) => b.seoScore - a.seoScore);
  }, [reviewsData, calculateSeoScore]);

  // Average SEO Score for Bar Chart Reference Line
  const avgSeoScore = useMemo(() => {
    const seoScores = businessesWithTraffic.map(business => calculateSeoScore(business));
    if (seoScores.length === 0) return 0;
    const sumOfSeoScores = seoScores.reduce((sum, score) => sum + score, 0);
    return sumOfSeoScores / seoScores.length;
  }, [businessesWithTraffic, calculateSeoScore]);

  // Filtered data for the details table
  const filteredDetailsData = useMemo(() => {
    if (!detailSearchQuery) {
      return sortedBusinesses;
    }
    return sortedBusinesses.filter(business =>
      business.name.toLowerCase().includes(detailSearchQuery.toLowerCase())
    );
  }, [sortedBusinesses, detailSearchQuery]);

  // Prepare traffic distribution data for BarChart (now SEO Score Distribution, sorted by SEO Score)
  const trafficDistributionData = useMemo(() => {
    return sortedBusinessesBySeoScore.slice(0, 20).map((business, index) => ({
      name: business.name.split(' ').slice(0, 2).join(' '),
      seoScore: business.seoScore, // Already calculated and sorted
      fill: COLORS[index % COLORS.length] // Use index from the new sorted array for consistent color
    }));
  }, [sortedBusinessesBySeoScore]);

  // Prepare pie chart data for PieChart (now SEO Score based, sorted by SEO Score)
  const pieChartData = useMemo(() => {
    return sortedBusinessesBySeoScore.slice(0, 10).map((business, index) => ({
      name: business.name.split(' ').slice(0, 2).join(' '),
      value: business.seoScore, // Value is now SEO score, already calculated and sorted
      fill: COLORS[index % COLORS.length] // Use index from the new sorted array for consistent color
    }));
  }, [sortedBusinessesBySeoScore]);

  // Prepare XY plot data comparing traffic and growth
  const xyPlotData = useMemo(() => {
    return sortedBusinesses.map(business => ({
      name: business.name.split(' ').slice(0, 2).join(' '),
      fullName: business.name,
      x: business.rating_count || 0,
      y: business.trafficValue || 0,
      fill: COLORS[sortedBusinesses.indexOf(business) % COLORS.length]
    }));
  }, [sortedBusinesses]);

  // --- START: NEW SEARCH RANKING METRICS ---
  const businessesWithSearchData = useMemo(() => {
    return reviewsData.filter(item => item.query_ranking_info && typeof item.query_ranking_info.url_ranking === 'number');
  }, [reviewsData]);

  const rankedInTop100 = useMemo(() => {
    return businessesWithSearchData.filter(item => item.query_ranking_info.url_ranking <= 100);
  }, [businessesWithSearchData]);

  const averageRanking = useMemo(() => {
    if (rankedInTop100.length === 0) return 0;
    const sumOfRanks = rankedInTop100.reduce((sum, item) => sum + item.query_ranking_info.url_ranking, 0);
    return sumOfRanks / rankedInTop100.length;
  }, [rankedInTop100]);

  const businessesTrackedCount = useMemo(() => {
    return reviewsData.filter(item => item.query_ranking_info).length;
  }, [reviewsData]);
  
  const visibilityRate = useMemo(() => {
    if (businessesTrackedCount === 0) return 0;
    return (rankedInTop100.length / businessesTrackedCount) * 100;
  }, [rankedInTop100, businessesTrackedCount]);

  const searchRankingDistribution = useMemo(() => {
    const categories = {
      "Top 10": { count: 0, color: COLORS[0] },
      "11-20": { count: 0, color: COLORS[1] },
      "21-50": { count: 0, color: COLORS[2] },
      "51-100": { count: 0, color: COLORS[3] },
      "Not Ranked": { count: 0, color: COLORS[4] },
    };
    reviewsData.forEach(item => {
      if (item.query_ranking_info) {
        const rank = item.query_ranking_info.url_ranking;
        if (typeof rank === 'number') {
          if (rank >= 1 && rank <= 10) categories["Top 10"].count++;
          else if (rank >= 11 && rank <= 20) categories["11-20"].count++;
          else if (rank >= 21 && rank <= 50) categories["21-50"].count++;
          else if (rank >= 51 && rank <= 100) categories["51-100"].count++;
          else categories["Not Ranked"].count++;
        } else {
          categories["Not Ranked"].count++;
        }
      } else {
        categories["Not Ranked"].count++;
      }
    });
    return Object.entries(categories).map(([name, data]) => ({ name, count: data.count, fill: data.color }));
  }, [reviewsData]);

  const topSearchRankings = useMemo(() => {
    return reviewsData
      .filter(item => (getTrafficValue(item) || 0) > 0) // Ensure traffic data for SEO calc
      .map(item => {
        return {
          ...item,
          estimatedSeoScore: calculateSeoScore(item) // Use the new centralized function
        };
      })
      .sort((a, b) => b.estimatedSeoScore - a.estimatedSeoScore)
      .slice(0, 5); // Top 5 by new SEO score
  }, [reviewsData, calculateSeoScore]);

  // --- END: NEW SEARCH RANKING METRICS ---

  // --- START: SCATTER PLOT METRICS (Traffic vs Reviews by Search Rank) ---
  const rankCategoryColors = {
    "Top 10": COLORS[0],     // Bright Blue
    "11-20": COLORS[1],    // Emerald Green
    "21-50": COLORS[10],   // Yellow
    "51-100": COLORS[4],   // Orange
    "Not Ranked": '#CBD5E0', // Gray
  };

  const getRankCategory = (rank) => {
    if (typeof rank !== 'number' || rank <= 0) return "Not Ranked";
    if (rank >= 1 && rank <= 10) return "Top 10";
    if (rank >= 11 && rank <= 20) return "11-20";
    if (rank >= 21 && rank <= 50) return "21-50";
    if (rank >= 51 && rank <= 100) return "51-100";
    return "Not Ranked";
  };

  const trafficReviewsScatterData = useMemo(() => {
    const mappedData = reviewsData
      .filter(business => 
        // calculateSeoScore handles 0 visitors, so no need to filter > 0 backlinks explicitly here
        business.rating_count > 0 &&
        business.query_ranking_info // Ensure query_ranking_info exists for rank category
      )
      .map(business => {
        const rank = business.query_ranking_info?.url_ranking;
        const category = getRankCategory(rank);
        return {
          name: business.name.split(' ').slice(0, 2).join(' '),
          fullName: business.name,
          x: business.rating_count || 0, // Total Reviews
          y: calculateSeoScore(business), // SEO Score
          rankCategory: category,
          fill: rankCategoryColors[category],
        };
      });
    
    const maxYValue = mappedData.reduce((max, p) => p.y > max ? p.y : max, 0);
    
    return { data: mappedData, maxY: maxYValue };
  }, [reviewsData, calculateSeoScore]);
  // --- END: SCATTER PLOT METRICS ---

  // --- START: COMPETITIVE INTELLIGENCE METRICS ---
  const businessesWithCompetitiveIntel = useMemo(() => {
    return reviewsData.filter(business => hasCompetitiveIntel(business));
  }, [reviewsData]);

  const avgDomainStrength = useMemo(() => {
    const businesses = businessesWithCompetitiveIntel;
    if (businesses.length === 0) return 0;
    const totalStrength = businesses.reduce((sum, business) => {
      return sum + getDomainStrength(business);
    }, 0);
    return totalStrength / businesses.length;
  }, [businessesWithCompetitiveIntel]);

  const totalOrganicValue = useMemo(() => {
    return businessesWithCompetitiveIntel.reduce((sum, business) => {
      return sum + getMonthlyOrganicValue(business);
    }, 0);
  }, [businessesWithCompetitiveIntel]);

  const totalOrganicKeywords = useMemo(() => {
    return businessesWithCompetitiveIntel.reduce((sum, business) => {
      return sum + getTotalOrganicKeywords(business);
    }, 0);
  }, [businessesWithCompetitiveIntel]);
  // --- END: COMPETITIVE INTELLIGENCE METRICS ---

  // Business Traffic Card component
  const BusinessTrafficCard = ({ business }) => {
    const visitors = getTrafficValue(business) || 0;
    const comparisonToAvg = (visitors / avgMonthlyVisitors * 100);
    const trafficColor = visitors > avgMonthlyVisitors
      ? "green.500"
      : visitors > avgMonthlyVisitors / 2
        ? "blue.500"
        : "gray.500";

    const social = business.traffic_data?.metrics?.social || {};
    const socialCount = Object.values(social).filter(Boolean).length;

    // Define hover background color using useColorModeValue at component level
    const hoverBg = useColorModeValue('gray.50', 'gray.700');

    // Determine badge color based on social count
    const socialBadgeColor = socialCount === 0
      ? "red"
      : socialCount === 1
        ? "yellow"
        : socialCount === 2
          ? "green"
          : "blue";

    // Competitive intelligence data
    const hasCompIntel = hasCompetitiveIntel(business);
    const domainStrength = hasCompIntel ? getDomainStrength(business) : null;
    const organicValue = hasCompIntel ? getMonthlyOrganicValue(business) : null;
    const strengthColor = domainStrength ? getDomainStrengthColor(domainStrength) : 'gray';

    return (
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        overflow="hidden"
        p={4}
        cursor="pointer"
        transition="all 0.2s"
        _hover={{ 
          transform: 'translateY(-4px)',
          shadow: 'lg',
          borderColor: 'blue.400',
          bg: hoverBg
        }}
        height="100%"
      >
        <VStack align="stretch" spacing={4} height="100%">
          <Box flex="1">
            <Heading
              size="md"
              mb={2}
              lineHeight="1.4"
              minHeight="2.8em"
              overflow="hidden"
              css={{
                display: '-webkit-box',
                WebkitLineClamp: '2',
                WebkitBoxOrient: 'vertical',
              }}
              title={business.name}
            >
              {business.name}
            </Heading>
            <HStack spacing={2} mb={2} justify="space-between" flexWrap="wrap">
              <Badge colorScheme="blue">
                {business.primary_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Business'}
              </Badge>
              <Badge colorScheme={socialBadgeColor}>
                {socialCount}/3 Social
              </Badge>
              {hasCompIntel && domainStrength !== null && (
                <Badge colorScheme={strengthColor} variant="solid">
                  {domainStrength} Strength
                </Badge>
              )}
            </HStack>
          </Box>

          {/* Traffic Metrics */}
          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={1}>Monthly Web Traffic</Text>
            
            {/* Traffic Bar */}
            <Box mb={3}>
              <Flex justify="space-between" mb={1}>
                <Text fontSize="sm">Site Traffic</Text>
                <Text fontSize="sm" fontWeight="bold">{visitors.toLocaleString()}</Text>
              </Flex>
              <Progress 
                value={comparisonToAvg > 100 ? 100 : comparisonToAvg} 
                colorScheme={trafficColor === "green.500" ? "green" : trafficColor === "blue.500" ? "blue" : "gray"} 
                size="sm" 
                borderRadius="full"
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                {comparisonToAvg.toFixed(0)}% of market average
              </Text>
            </Box>
            
            {/* Social Media Icons */}
            <HStack mt={3} spacing={4} justify="center">
              <Icon 
                as={FaFacebook} 
                boxSize={6} 
                color={social.facebook ? "blue.500" : "gray.300"} 
                title={social.facebook ? "Facebook Page Available" : "No Facebook Page"}
              />
              <Icon 
                as={FaInstagram} 
                boxSize={6} 
                color={social.instagram ? "purple.500" : "gray.300"} 
                title={social.instagram ? "Instagram Account Available" : "No Instagram Account"}
              />
              <Icon 
                as={FaTwitter} 
                boxSize={6} 
                color={social.twitter ? "blue.400" : "gray.300"} 
                title={social.twitter ? "Twitter Account Available" : "No Twitter Account"}
              />
            </HStack>
          </Box>

          {/* Competitive Intelligence Metrics (if available) */}
          {hasCompIntel && organicValue > 0 && (
            <Box bg={statBg} p={3} borderRadius="md">
              <Text fontSize="xs" fontWeight="medium" color="purple.600" mb={1}>
                Organic Value
              </Text>
              <Text fontSize="lg" fontWeight="bold" color="purple.700">
                {formatCurrency(organicValue)}/mo
              </Text>
            </Box>
          )}

          {/* Basic Info */}
          <VStack align="start" spacing={1} mt="auto" fontSize="sm">
            <HStack spacing={2}>
              <Icon as={FaGlobe} color="gray.500" boxSize={4} />
              <Text>
                {business.traffic_data?.metrics?.page_info?.status_code === 200 ?
                  "Website Active" : "Website Issue"}
              </Text>
            </HStack>
            <HStack spacing={2}>
              <Icon as={FaStar} color="gray.500" boxSize={4} />
              <Text>{business.rating_count} reviews</Text>
            </HStack>
          </VStack>
        </VStack>
      </Box>
    );
  };

  // Handler for clicking on a business to open BusinessReviewModal
  const handleBusinessClick = useCallback((business) => {
    if (!business) return;

    const businessMapsUri = business.maps_uri || business.mapsUri;
    const businessName = business.business_name || business.name;
    const businessData =
      combinedData.find(b => businessMapsUri && b.maps_uri === businessMapsUri) ||
      combinedData.find(b => businessName && b.name === businessName) ||
      business;
    if (businessData) {
      setSelectedBusiness(businessData);
      setIsModalOpen(true);
    }
  }, [combinedData]);

  const handleRowClick = (row) => {
    handleBusinessClick(row);
  };

  // Card heights for descending effect (tallest to shortest)
  const cardHeights = ['340px', '310px', '280px', '250px', '220px'];
  const cardPaddings = [6, 5, 4, 4, 3];
  const iconSizes = [8, 7, 6, 5, 5];
  const scoreFontSizes = ['3xl', '2xl', 'xl', 'lg', 'lg'];
  const headingSizes = ['md', 'sm', 'sm', 'xs', 'xs'];
  const badgeSizes = ['32px', '28px', '26px', '24px', '22px'];
  const topBarHeights = ['6px', '5px', '4px', '3px', '3px'];
  
  // Colors for each position
  const positionColors = [
    { gradient: 'linear(to-r, teal.300, cyan.400, teal.400)', border: 'teal.200', icon: 'teal.400', badge: 'linear(to-br, teal.300, cyan.400)', text: 'teal.500' },
    { gradient: 'linear(to-r, gray.300, gray.500, gray.300)', border: 'gray.300', icon: 'gray.400', badge: 'linear(to-br, gray.300, gray.500)', text: 'gray.600' },
    { gradient: 'linear(to-r, orange.200, orange.400, orange.200)', border: 'orange.200', icon: 'orange.400', badge: 'linear(to-br, orange.300, orange.500)', text: 'orange.500' },
    { gradient: 'linear(to-r, blue.200, blue.400, blue.200)', border: 'blue.200', icon: 'blue.400', badge: 'linear(to-br, blue.300, blue.500)', text: 'blue.500' },
    { gradient: 'linear(to-r, purple.200, purple.400, purple.200)', border: 'purple.200', icon: 'purple.400', badge: 'linear(to-br, purple.300, purple.500)', text: 'purple.500' },
  ];
  
  const positionLabels = ['SEO Champion', '#2 SEO', '#3 SEO', '#4 SEO', '#5 SEO'];

  return (
    <Box>
      {/* SEO Podium - Top 5 SEO Rankings - Descending Left to Right */}
      <Box
        display={{ base: 'none', md: 'block' }}
        bg='transparent'
        mb={8}
      >
        <HStack spacing={2} mb={6} align='center'>
          <Box
            bgGradient='linear(to-r, teal.500, cyan.500)'
            p={2}
            borderRadius='lg'
          >
            <Icon as={FaTrophy} color='white' boxSize={5} />
          </Box>
          <Heading 
            size='lg' 
            bgGradient='linear(to-r, teal.600, cyan.600)'
            bgClip='text'
            fontWeight='bold'
          >
            SEO Leaders
          </Heading>
        </HStack>
        
        {topSearchRankings.length > 0 ? (
          <Flex justify='center' align='flex-end' gap={4} w='full'>
            {topSearchRankings.slice(0, 5).map((business, index) => (
              <Box
                key={business.maps_uri || index}
                bg={cardBg}
                borderRadius='2xl'
                textAlign='center'
                position='relative'
                cursor='pointer'
                onClick={() => handleBusinessClick(business)}
                transition='all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                _hover={{ 
                  transform: 'translateY(-8px)', 
                  shadow: '2xl',
                }}
                overflow='hidden'
                flex='1'
                maxW='240px'
                h={cardHeights[index]}
                boxShadow={index === 0 ? '0 20px 40px -12px rgba(56, 178, 172, 0.3)' : 'xl'}
                borderWidth='1px'
                borderColor={positionColors[index].border}
              >
                <Box 
                  h={topBarHeights[index]} 
                  bgGradient={positionColors[index].gradient}
                  backgroundSize={index === 0 ? '200% 100%' : 'auto'}
                  animation={index === 0 ? 'shimmer 2s linear infinite' : 'none'}
                  sx={index === 0 ? {
                    '@keyframes shimmer': {
                      '0%': { backgroundPosition: '200% 0' },
                      '100%': { backgroundPosition: '-200% 0' },
                    },
                  } : {}}
                />
                <VStack p={cardPaddings[index]} spacing={2} h={`calc(100% - ${topBarHeights[index]})`} justify='space-between'>
                  <Flex
                    position='absolute'
                    top={2}
                    left={2}
                    w={badgeSizes[index]}
                    h={badgeSizes[index]}
                    borderRadius='full'
                    bgGradient={positionColors[index].badge}
                    justify='center'
                    align='center'
                    shadow='md'
                  >
                    <Text color='white' fontWeight='bold' fontSize={index < 2 ? 'sm' : 'xs'}>{index + 1}</Text>
                  </Flex>
                  
                  <VStack spacing={1} flex='1' justify='center'>
                    <Icon 
                      as={index === 0 ? FaTrophy : FaBullseye} 
                      color={positionColors[index].icon} 
                      boxSize={iconSizes[index]} 
                      filter={index === 0 ? 'drop-shadow(0 2px 4px rgba(56, 178, 172, 0.5))' : 'none'}
                    />
                    
                    <Text 
                      fontSize='xs' 
                      color={positionColors[index].text} 
                      fontWeight='bold' 
                      textTransform='uppercase' 
                      letterSpacing='wider'
                    >
                      {positionLabels[index]}
                    </Text>
                    
                    <Tooltip label={business.name} placement='top' hasArrow openDelay={300}>
                      <Heading 
                        size={headingSizes[index]} 
                        noOfLines={2} 
                        color='gray.700' 
                        minH={index < 2 ? '44px' : '36px'}
                      >
                        {business.name}
                      </Heading>
                    </Tooltip>
                    
                    <Text 
                      fontWeight='extrabold' 
                      fontSize={scoreFontSizes[index]} 
                      bgGradient={`linear(to-r, ${positionColors[index].text}, ${positionColors[index].icon})`}
                      bgClip='text'
                    >
                      {business.estimatedSeoScore?.toFixed(1) ?? '0'}
                    </Text>
                    <Text fontSize='xs' color='gray.500' fontWeight='medium' textTransform='uppercase' letterSpacing='wider'>
                      Est. SEO Score
                    </Text>
                  </VStack>
                  
                  <VStack spacing={1}>
                    <HStack justify="center" spacing={2} fontSize="xs" color="gray.600">
                      <Text>
                        Rank: {business.query_ranking_info?.url_ranking
                          ? `#${business.query_ranking_info.url_ranking}`
                          : 'Unranked (>100)'}
                      </Text>
                      <Text>{(getTrafficValue(business) || 0).toLocaleString()}/mo</Text>
                    </HStack>
                    
                    <HStack spacing={3} justify="center">
                      {business.traffic_data?.metrics?.social?.facebook && (
                        <Icon as={FaFacebook} boxSize={index < 2 ? 4 : 3} color="blue.500" />
                      )}
                      {business.traffic_data?.metrics?.social?.instagram && (
                        <Icon as={FaInstagram} boxSize={index < 2 ? 4 : 3} color="purple.500" />
                      )}
                    </HStack>
                  </VStack>
                </VStack>
              </Box>
            ))}
          </Flex>
        ) : (
          <Box 
            bg={cardBg}
            borderRadius='lg'
            p={8}
            textAlign='center'
            borderWidth='1px'
            borderColor={borderColor}
          >
            <Text color="gray.500">
              No businesses found with sufficient data for SEO ranking showcase.
            </Text>
          </Box>
        )}
      </Box>

      {/* Section Label */}
      {!demoMode && <SectionLabel description="Analyze your search engine visibility and local SEO performance. Track keyword rankings, Google Business Profile optimization, and online discoverability.">SEO</SectionLabel>}

      {/* Main Content Area with Tabs */}
      <Box 
        bg={cardBg} 
        borderRadius="lg" 
        shadow="md" 
        borderWidth="1px"
        borderColor={borderColor}
        mb={8} 
        overflow="hidden"
      >
        <Tabs>
          <TabList px={4} pt={4}>
            <Tab fontWeight="medium">Overview</Tab>
            <Tab fontWeight="medium">Businesses</Tab>
            <Tab fontWeight="medium">Trends</Tab>
            <Tab fontWeight="medium">Details</Tab>
            <Tab fontWeight="medium">Methodology</Tab>
          </TabList>

          <TabPanels>
            {/* Overview Panel */}
            <TabPanel>
              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
                {/* SEO Score Distribution Chart */}
                <Box height={{ base: "280px", md: "400px" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={trafficDistributionData} // Now contains seoScore
                      margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
                      animationDuration={1000}
                      animationBegin={0}
                      animationEasing="ease-out"
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis 
                        dataKey="name" 
                        angle={-45}
                        textAnchor="end"
                        height={60}
                        interval={0}
                        tick={{ fill: '#2D3748', fontSize: 14, fontWeight: 600 }}
                      />
                      <YAxis tick={{ fill: '#2D3748', fontSize: 14, fontWeight: 600 }} domain={[0, (dataMax) => Math.ceil(dataMax + 10)]} />
                      <RechartsTooltip 
                        formatter={(value) => typeof value === 'number' ? value.toFixed(1) : value} // Format SEO score
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <Box 
                                bg="white" 
                                p={3} 
                                boxShadow="lg" 
                                border="1px" 
                                borderColor="gray.200" 
                                borderRadius="md"
                              >
                                <Text fontWeight="bold">{payload[0].payload.name}</Text>
                                <Text>
                                  {payload[0].value.toFixed(1)} SEO Score
                                </Text>
                                <Text color="orange.500">
                                  Avg. SEO Score: {avgSeoScore.toFixed(1)}
                                </Text>
                              </Box>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar 
                        dataKey="seoScore" // Changed from visitors/backlinks to seoScore
                        cursor="pointer"
                        isAnimationActive={true}
                        animationDuration={1000}
                        animationBegin={300}
                        animationEasing="ease-out"
                        label={FavoriteStarBarLabel}
                      >
                        {trafficDistributionData.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.fill} 
                          />
                        ))}
                      </Bar>
                      <ReferenceLine 
                        y={avgSeoScore} // Use average SEO score
                        stroke="#ED8936" 
                        strokeDasharray="5 5"
                        strokeWidth={2}
                        label={{ 
                          value: 'Avg. SEO Score', // Updated label
                          position: 'right',
                          fill: '#ED8936'
                        }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>

                {/* SEO Score Leaders Pie Chart */}
                <Box height={{ base: "280px", md: "400px" }} overflow="visible">
                  <ResponsiveContainer width="100%" height="100%" style={{ overflow: 'visible' }}>
                    <PieChart style={{ overflow: 'visible' }}>
                      <Pie
                        data={pieChartData} // Now contains seoScore as value
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={120}
                        paddingAngle={2}
                        dataKey="value" // value is now SEO score
                        label={FavoriteStarPieLabel}
                        labelLine={{ stroke: '#4A5568', strokeWidth: 1 }}
                        isAnimationActive={true}
                        animationBegin={300}
                        animationDuration={800}
                        animationEasing="ease-out"
                        cursor="pointer"
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <RechartsTooltip 
                        formatter={(value) => `${value.toFixed(1)} SEO Score`} // value is SEO score
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #E2E8F0',
                          borderRadius: '4px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </SimpleGrid>
            </TabPanel>

            {/* Businesses Panel */}
            <TabPanel>
              <Box h={{ base: "350px", md: "600px" }}>
                <BusinessBubbleChart
                  businesses={sortedBusinessesBySeoScore}
                  valueKey="seoScore"
                  valueLabel="SEO Score"
                  onBusinessClick={handleRowClick}
                  formatValue={(val) => val?.toFixed(1) || '0'}
                  colorScheme="teal"
                />
              </Box>
            </TabPanel>

            {/* Trends Panel */}
            <TabPanel>
              <Box mb={8}> {/* Existing Scatter Plot for Traffic vs Growth - keeping it separate for now */}
                <Text color="gray.600" mb={6}>
                  Explore the relationship between review count and monthly web traffic
                </Text>
                <Box height={{ base: "350px", md: "600px" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart
                      margin={{ top: 20, right: 40, bottom: 60, left: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis 
                        type="number" 
                        dataKey="x" 
                        name="Total Reviews" 
                        unit=" reviews"
                        label={{ 
                          value: 'Total Reviews', 
                          position: 'bottom', 
                          offset: 40,
                          fill: '#2D3748',
                          fontWeight: 600
                        }}
                        tick={{ fill: '#2D3748', fontSize: 14, fontWeight: 600 }}
                      />
                      <YAxis 
                        type="number" 
                        dataKey="y" 
                        name="Monthly Traffic" 
                        unit=" visits"
                        label={{ 
                          value: 'Monthly Traffic', 
                          angle: -90, 
                          position: 'left', 
                          offset: 40,
                          fill: '#2D3748',
                          fontWeight: 600
                        }}
                        tick={{ fill: '#2D3748', fontSize: 14, fontWeight: 600 }}
                      />
                      <RechartsTooltip 
                        cursor={{ strokeDasharray: '3 3' }}
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <Box 
                                bg={cardBg} 
                                p={3} 
                                boxShadow="lg" 
                                border="1px" 
                                borderColor={borderColor} 
                                borderRadius="md"
                              >
                                <Text fontWeight="bold">{data.fullName}</Text>
                                <Text>Reviews: {data.x.toLocaleString()}</Text>
                                <Text>Monthly Traffic: {data.y.toLocaleString()}</Text>
                              </Box>
                            );
                          }
                          return null;
                        }}
                      />
                      <Scatter 
                        name="Businesses" 
                        data={xyPlotData} 
                        cursor="pointer"
                      >
                        {xyPlotData.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.fill} 
                            fillOpacity={0.8}
                            stroke={entry.fill}
                            strokeWidth={1}
                          />
                        ))}
                      </Scatter>
                      <ReferenceLine 
                        y={avgMonthlyVisitors} 
                        stroke="#ED8936" 
                        strokeDasharray="5 5"
                        strokeWidth={1}
                        label={{ 
                          value: 'Avg Traffic', 
                          position: 'right',
                          fill: '#ED8936',
                          fontSize: 11
                        }}
                      />
                    </ScatterChart>
                  </ResponsiveContainer>
                </Box>
              </Box>

              {/* --- START: NEW SCATTER PLOT (Backlinks vs Reviews by Search Rank) --- */}
              <Box borderTopWidth="1px" borderColor={borderColor} pt={8} mt={8}>
                <Heading size="md" mb={2}>Estimated SEO Score vs Reviews by Search Rank</Heading>
                <Text color="gray.600" mb={6}>
                  Explore how search ranking affects the relationship between review count and <Tooltip label="SEO Score is estimated by dividing Monthly Traffic by Search Rank (unranked or ranks >100 are treated as 100 for this calculation; businesses with zero traffic have an SEO Score of 0)." placement="top" hasArrow><Text as="span" fontWeight="bold" textDecoration="underline dotted">SEO Score</Text></Tooltip>.
                </Text>
                <Box height={{ base: "350px", md: "600px" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart
                      margin={{ top: 60, right: 40, bottom: 60, left: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={borderColor} />
                      <XAxis 
                        type="number" 
                        dataKey="x" 
                        name="Total Reviews" 
                        unit=" reviews"
                        label={{ 
                          value: 'Total Reviews', 
                          position: 'bottom', 
                          offset: 40,
                          fill: '#2D3748',
                          fontWeight: 600
                        }}
                        tick={{ fill: '#2D3748', fontSize: 14, fontWeight: 600 }}
                      />
                      <YAxis 
                        type="number" 
                        dataKey="y" 
                        name="SEO Score" 
                        scale="log"
                        domain={[0.1, trafficReviewsScatterData.maxY > 0 ? trafficReviewsScatterData.maxY * 1.2 : 'auto']}
                        allowDataOverflow={true}
                        label={{ 
                          value: 'SEO Score', 
                          angle: -90, 
                          position: 'left', 
                          offset: 40,
                          fill: '#2D3748',
                          fontWeight: 600
                        }}
                        tick={{ fill: '#2D3748', fontSize: 14, fontWeight: 600 }}
                      />
                      <RechartsTooltip 
                        cursor={{ strokeDasharray: '3 3' }}
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <Box 
                                bg={cardBg} 
                                p={3} 
                                boxShadow="lg" 
                                border="1px" 
                                borderColor={borderColor} 
                                borderRadius="md"
                              >
                                <Text fontWeight="bold">{data.fullName}</Text>
                                <Text>Reviews: {data.x.toLocaleString()}</Text>
                                <Text>SEO Score: {typeof data.y === 'number' && data.y > 0 ? data.y.toFixed(1) : '<1.0'}</Text>
                                <Text>Search Rank: <Badge colorScheme={rankCategoryColors[data.rankCategory] === '#CBD5E0' ? 'gray' : 'blue'} variant="solid" style={{backgroundColor: rankCategoryColors[data.rankCategory]}}>{data.rankCategory}</Badge></Text>
                              </Box>
                            );
                          }
                          return null;
                        }}
                      />
                      <Legend 
                        iconSize={10} 
                        wrapperStyle={{ bottom: 0, left: 20, right: 20, paddingTop: '20px' }} 
                        payload={Object.entries(rankCategoryColors).map(([name, color]) => ({
                          value: name,
                          type: 'scatter',
                          id: name,
                          color: color,
                        }))}
                      />
                      <Scatter 
                        name="Businesses" 
                        data={trafficReviewsScatterData.data} 
                        cursor="pointer"
                      >
                        {trafficReviewsScatterData.data.map((entry, index) => (
                          <Cell 
                            key={`cell-traffic-reviews-${index}`} 
                            fill={entry.fill} 
                            fillOpacity={0.8}
                            stroke={entry.fill}
                            strokeWidth={1}
                          />
                        ))}
                      </Scatter>
                    </ScatterChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
              {/* --- END: NEW SCATTER PLOT --- */}
            </TabPanel>

            {/* Details Panel */}
            <TabPanel>
              <ModernDetailsTable
                data={sortedBusinesses}
                columns={columns}
                onRowClick={handleRowClick}
                accentColor="teal"
                defaultSortColumnId={2}
                defaultSortAsc={false}
                searchPlaceholder="Search businesses..."
              />
            </TabPanel>

            {/* Methodology Panel */}
            <TabPanel>
              <VStack spacing={6} align="stretch" maxW="900px" mx="auto">
                <Box>
                  <Heading size="lg" mb={4} color={methodologyHeadingColor}>
                    SEO Dashboard Methodology
                  </Heading>
                  <Text color={methodologyTextColor} mb={6}>
                    Understanding how we measure and analyze your online visibility metrics.
                  </Text>
                </Box>

                {/* Core Metrics Section */}
                <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
                  <CardBody>
                    <HStack mb={4}>
                      <Icon as={FaChartBar} color="blue.500" boxSize={6} />
                      <Heading size="md">Core Metrics</Heading>
                    </HStack>
                    <VStack align="stretch" spacing={4}>
                      <Box>
                        <Text fontWeight="bold" color={textColor}>Monthly Traffic</Text>
                        <Text color={subtitleColor} fontSize="sm">
                          The estimated number of monthly visits a business website receives from organic search,
                          based on SpyFu click estimates. Higher traffic indicates stronger online visibility.
                        </Text>
                      </Box>
                      <Box>
                        <Text fontWeight="bold" color={textColor}>Search Rank</Text>
                        <Text color={subtitleColor} fontSize="sm">
                          The business's position in search results for location-based queries. 
                          Rankings 1-10 appear on the first page, while ranks above 100 or unranked businesses 
                          are treated as position 100 for calculations.
                        </Text>
                      </Box>
                      <Box>
                        <Text fontWeight="bold" color={textColor}>Social Media Presence</Text>
                        <Text color={subtitleColor} fontSize="sm">
                          Tracks active profiles on Facebook, Instagram, and Twitter. 
                          Each platform contributes to the business's digital footprint score (0-3).
                        </Text>
                      </Box>
                    </VStack>
                  </CardBody>
                </Card>

                {/* SEO Score Calculation */}
                <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
                  <CardBody>
                    <HStack mb={4}>
                      <Icon as={FaBullseye} color="green.500" boxSize={6} />
                      <Heading size="md">SEO Score Calculation</Heading>
                    </HStack>
                    <VStack align="stretch" spacing={4}>
                      <Box>
                        <Text fontWeight="bold" color={textColor}>Formula</Text>
                        <Box bg={statBg} p={3} borderRadius="md" fontFamily="mono" fontSize="sm">
                          SEO Score = Monthly Traffic ÷ Search Rank
                        </Box>
                        <Text color={subtitleColor} fontSize="sm" mt={2}>
                          This score balances traffic volume with search visibility. A business with 1,000 
                          monthly visits ranked #10 scores 100, while the same traffic ranked #50 scores only 20.
                        </Text>
                      </Box>
                      <Box>
                        <Text fontWeight="bold" color={textColor}>Special Cases</Text>
                        <VStack align="stretch" spacing={2} mt={2}>
                          <HStack>
                            <Badge colorScheme="orange">Zero Traffic</Badge>
                            <Text fontSize="sm" color={subtitleColor}>SEO Score = 0</Text>
                          </HStack>
                          <HStack>
                            <Badge colorScheme="purple">Unranked/Rank 100</Badge>
                            <Text fontSize="sm" color={subtitleColor}>Treated as rank 100</Text>
                          </HStack>
                        </VStack>
                      </Box>
                    </VStack>
                  </CardBody>
                </Card>

                {/* Data Sources */}
                <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
                  <CardBody>
                    <HStack mb={4}>
                      <Icon as={FaGlobe} color="purple.500" boxSize={6} />
                      <Heading size="md">Data Sources & Analysis</Heading>
                    </HStack>
                    <VStack align="stretch" spacing={4}>
                      <Box>
                        <Text fontWeight="bold" color={textColor}>Traffic Data</Text>
                        <Text color={subtitleColor} fontSize="sm">
                          Sourced from SpyFu monthly organic click estimates, with a fallback to
                          legacy crawler-based estimates when SpyFu data is unavailable.
                        </Text>
                      </Box>
                      <Box>
                        <Text fontWeight="bold" color={textColor}>Search Rankings</Text>
                        <Text color={subtitleColor} fontSize="sm">
                          Based on local search queries relevant to each business category. 
                          Rankings reflect visibility in the primary service area.
                        </Text>
                      </Box>
                      <Box>
                        <Text fontWeight="bold" color={textColor}>Domain Analysis</Text>
                        <Text color={subtitleColor} fontSize="sm">
                          Optional SpyFu integration provides deeper insights including organic clicks, 
                          domain strength (0-100 scale), and competitive intelligence data.
                        </Text>
                      </Box>
                    </VStack>
                  </CardBody>
                </Card>

                {/* Interpretation Guide */}
                <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
                  <CardBody>
                    <HStack mb={4}>
                      <Icon as={FaLightbulb} color="yellow.500" boxSize={6} />
                      <Heading size="md">Interpreting Your Results</Heading>
                    </HStack>
                    <VStack align="stretch" spacing={4}>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <Box>
                          <HStack mb={2}>
                            <Badge colorScheme="green">High SEO Score (100+)</Badge>
                          </HStack>
                          <Text fontSize="sm" color={subtitleColor}>
                            Strong online visibility with good balance of traffic and search ranking. 
                            Continue current strategies while exploring growth opportunities.
                          </Text>
                        </Box>
                        <Box>
                          <HStack mb={2}>
                            <Badge colorScheme="blue">Medium SEO Score (20-100)</Badge>
                          </HStack>
                          <Text fontSize="sm" color={subtitleColor}>
                            Moderate visibility with room for improvement. Focus on either increasing 
                            traffic or improving search rankings.
                          </Text>
                        </Box>
                        <Box>
                          <HStack mb={2}>
                            <Badge colorScheme="orange">Low SEO Score (5-20)</Badge>
                          </HStack>
                          <Text fontSize="sm" color={subtitleColor}>
                            Limited online presence. Prioritize basic SEO improvements like claiming 
                            listings and building initial backlinks.
                          </Text>
                        </Box>
                        <Box>
                          <HStack mb={2}>
                            <Badge colorScheme="red">Very Low SEO Score (&lt;5)</Badge>
                          </HStack>
                          <Text fontSize="sm" color={subtitleColor}>
                            Minimal online visibility. Start with fundamental digital presence: 
                            website optimization and directory listings.
                          </Text>
                        </Box>
                      </SimpleGrid>
                    </VStack>
                  </CardBody>
                </Card>

                {/* Best Practices */}
                <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
                  <CardBody>
                    <HStack mb={4}>
                      <Icon as={FaFireAlt} color="orange.500" boxSize={6} />
                      <Heading size="md">SEO Best Practices</Heading>
                    </HStack>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <VStack align="stretch" spacing={3}>
                        <Text fontWeight="bold" color={textColor}>To Increase Traffic:</Text>
                        <VStack align="stretch" spacing={2} pl={4}>
                          <Text fontSize="sm" color={subtitleColor}>• Create valuable, shareable content</Text>
                          <Text fontSize="sm" color={subtitleColor}>• Partner with local businesses</Text>
                          <Text fontSize="sm" color={subtitleColor}>• Get listed in quality directories</Text>
                          <Text fontSize="sm" color={subtitleColor}>• Engage with local media</Text>
                        </VStack>
                      </VStack>
                      <VStack align="stretch" spacing={3}>
                        <Text fontWeight="bold" color={textColor}>To Improve Rankings:</Text>
                        <VStack align="stretch" spacing={2} pl={4}>
                          <Text fontSize="sm" color={subtitleColor}>• Optimize Google Business Profile</Text>
                          <Text fontSize="sm" color={subtitleColor}>• Focus on local keywords</Text>
                          <Text fontSize="sm" color={subtitleColor}>• Improve website speed</Text>
                          <Text fontSize="sm" color={subtitleColor}>• Build consistent NAP citations</Text>
                        </VStack>
                      </VStack>
                    </SimpleGrid>
                  </CardBody>
                </Card>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
        {/* --- START: NEW SEARCH RANKING SUMMARY STATS (MOVED DOWN) --- */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={8}>
        <MetricCard
          icon={FaSearch}
          title="Businesses Tracked"
          value={businessesTrackedCount.toLocaleString()}
          description="Total businesses with search data"
          colorScheme="cyan"
        />
        <MetricCard
          icon={FaChartBar}
          title="Ranked in Top 100"
          value={rankedInTop100.length.toLocaleString()}
          description={`${visibilityRate.toFixed(1)}% visibility rate`}
          colorScheme="green"
        />
        <MetricCard
          icon={FaListOl}
          title="Average Ranking"
          value={averageRanking > 0 ? averageRanking.toFixed(1) : 'N/A'}
          description="Among ranked businesses"
          colorScheme="orange"
        />
      </SimpleGrid>
      {/* --- END: NEW SEARCH RANKING SUMMARY STATS --- */}
          {/* Market Overview Stats */}
          {/* <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={8}>
        <StatCard
          icon={FaGlobe}
          title="Total Monthly Backlinks"
          value={totalMonthlyVisitors.toLocaleString()}
          description={`Across ${businessesWithTraffic.length} businesses`}
          colorScheme="blue"
        />
        <StatCard
          icon={FaChartLine}
          title="Average Monthly Backlinks"
          value={avgMonthlyVisitors.toFixed(0)}
          description="Per business"
          colorScheme="green"
        />
        <StatCard
          icon={FaFireAlt}
          title="Median Monthly Backlinks"
          value={medianMonthlyVisitors.toLocaleString()}
          description="Market middle point"
          colorScheme="purple"
        />
      </SimpleGrid> */}
      {/* --- START: ENHANCED SEARCH RANKING SECTION --- */}
      <Box mb={8}>
        <Flex align="center" mb={6}>
          <Icon as={FaSearch} color="blue.500" boxSize={6} mr={3} />
          <Heading size="lg" color="gray.700">SEO Analytics</Heading>
        </Flex>
        
        {/* Grid layout changed to 1fr to make chart wider */}
        <Grid templateColumns={{ base: "1fr" }} gap={8}> 
          
          {/* Enhanced Search Ranking Distribution Chart REPLACED WITH SCATTER PLOT */}
          <Box 
            bg={cardBg} 
            p={8} 
            borderRadius="xl" 
            shadow="lg" 
            borderWidth="1px" 
            borderColor={borderColor}
            position="relative"
            overflow="hidden"
          >
            {/* Background gradient (optional, can be adjusted or removed for scatter) */}
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              height="4px"
              bgGradient="linear(to-r, purple.400, teal.400, green.400)" // Adjusted gradient
            />
            
            <VStack align="stretch" spacing={6}>
              <HStack justify="space-between" align="center">
                <VStack align="start" spacing={1}>
                  {/* Title updated for Scatter Plot */}
                  <Heading size="md" color="gray.700">Estimated SEO Score vs Reviews by Search Rank</Heading>
                  <Text fontSize="sm" color="gray.500">
                    Explore how search ranking affects the relationship between review count and <Tooltip label="SEO Score is estimated by dividing Monthly Traffic by Search Rank (unranked or ranks >100 are treated as 100 for this calculation; businesses with zero traffic have an SEO Score of 0)." placement="top" hasArrow><Text as="span" fontWeight="bold" textDecoration="underline dotted">SEO Score</Text></Tooltip>.
                  </Text>
                </VStack>
                {/* Badge can be adapted or removed if not relevant to scatter */}
                <Badge 
                  colorScheme="teal" 
                  variant="subtle" 
                  px={3} 
                  py={1} 
                  borderRadius="full"
                  fontSize="sm"
                >
                  {trafficReviewsScatterData.data.length} Data Points
                </Badge>
              </HStack>

              {/* Scatter Plot Container */}
              <Box 
                height={{ base: "350px", md: "620px" }}
                bg={statBg}
                borderRadius="lg"
                p={4}
                position="relative"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart
                    margin={{ top: 60, right: 40, bottom: 60, left: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={borderColor} />
                    <XAxis 
                      type="number" 
                      dataKey="x" 
                      name="Total Reviews" 
                      unit=" reviews"
                      label={{ 
                        value: 'Total Reviews', 
                        position: 'bottom', 
                        offset: 40,
                        fill: '#2D3748',
                        fontWeight: 600
                      }}
                      tick={{ fill: '#2D3748', fontSize: 14, fontWeight: 600 }}
                    />
                    <YAxis 
                      type="number" 
                      dataKey="y" 
                      name="SEO Score" 
                      scale="log"
                      domain={[0.1, trafficReviewsScatterData.maxY > 0 ? trafficReviewsScatterData.maxY * 1.2 : 'auto']}
                      allowDataOverflow={true}
                      label={{ 
                        value: 'SEO Score', 
                        angle: -90, 
                        position: 'left', 
                        offset: 40,
                        fill: '#2D3748',
                        fontWeight: 600
                      }}
                      tick={{ fill: '#2D3748', fontSize: 14, fontWeight: 600 }}
                    />
                    <RechartsTooltip 
                      cursor={{ strokeDasharray: '3 3' }}
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <Box 
                              bg={cardBg} 
                              p={3} 
                              boxShadow="lg" 
                              border="1px" 
                              borderColor={borderColor} 
                              borderRadius="md"
                            >
                              <Text fontWeight="bold">{data.fullName}</Text>
                              <Text>Reviews: {data.x.toLocaleString()}</Text>
                              <Text>SEO Score: {typeof data.y === 'number' ? data.y.toFixed(1) : 'N/A'}</Text>
                              <Text>Search Rank: <Badge colorScheme={rankCategoryColors[data.rankCategory] === '#CBD5E0' ? 'gray' : 'blue'} variant="solid" style={{backgroundColor: rankCategoryColors[data.rankCategory]}}>{data.rankCategory}</Badge></Text>
                            </Box>
                          );
                        }
                        return null;
                      }}
                    />
                    <Legend 
                      iconSize={10} 
                      wrapperStyle={{ bottom: 0, left: 20, right: 20, paddingTop: '20px' }} 
                      payload={Object.entries(rankCategoryColors).map(([name, color]) => ({
                        value: name,
                        type: 'scatter',
                        id: name,
                        color: color,
                      }))}
                    />
                    <Scatter 
                      name="Businesses" 
                      data={trafficReviewsScatterData.data} 
                      cursor="pointer"
                    >
                      {trafficReviewsScatterData.data.map((entry, index) => (
                        <Cell 
                          key={`cell-traffic-reviews-${index}`} 
                          fill={entry.fill} 
                          fillOpacity={0.8}
                          stroke={entry.fill}
                          strokeWidth={1}
                        />
                      ))}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              </Box>

              {/* REMOVED: Summary Stats Row for old bar chart */}
              {/* <SimpleGrid columns={3} spacing={4}> ... </SimpleGrid> */}
            </VStack>
          </Box>
          
          {/* REMOVED: Enhanced Top Search Rankings List (VStack) as it's now integrated into the top showcase */}

        </Grid>
      </Box>
      {/* --- END: ENHANCED SEARCH RANKING SECTION --- */}

      {/* --- START: COMPETITIVE INTELLIGENCE OVERVIEW --- */}
      {businessesWithCompetitiveIntel.length > 0 && (
        <Box mb={8}>
          <Flex align="center" mb={6}>
            <Icon as={FaBullseye} color="purple.500" boxSize={6} mr={3} />
            <Heading size="lg" color="gray.700">Competitive Intelligence Overview</Heading>
          </Flex>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            <MetricCard
              icon={FaChartLine}
              title="Businesses Tracked"
              value={businessesWithCompetitiveIntel.length.toLocaleString()}
              description={`${((businessesWithCompetitiveIntel.length / reviewsData.length) * 100).toFixed(1)}% of market`}
              colorScheme="purple"
            />
            <MetricCard
              icon={FaTrophy}
              title="Avg Domain Strength"
              value={avgDomainStrength.toFixed(1)}
              description="0-100 scale"
              colorScheme={getDomainStrengthColor(avgDomainStrength)}
            />
            <MetricCard
              icon={FaFireAlt}
              title="Total Organic Value"
              value={formatCurrency(totalOrganicValue)}
              description="Est. monthly across market"
              colorScheme="green"
            />
            <MetricCard
              icon={FaSearch}
              title="Keywords Tracked"
              value={formatNum(totalOrganicKeywords)}
              description="Organic search terms"
              colorScheme="blue"
            />
          </SimpleGrid>

          <Box
            mt={6}
            bg={cardBg}
            p={4}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <Text fontSize="sm" color="gray.600">
              <Icon as={InfoOutlineIcon} mr={2} />
              Competitive intelligence data provides insights into organic search performance,
              domain authority, and keyword rankings. Businesses with this data have enhanced
              market analysis capabilities.
            </Text>
          </Box>
        </Box>
      )}
      {/* --- END: COMPETITIVE INTELLIGENCE OVERVIEW --- */}

      {/* Business Review Modal */}
      <BusinessReviewModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedBusiness(null);
        }}
        business={selectedBusiness}
        yearData={yearData}
        sortedByTotal={sortedByTotal}
        sortedBySentiment={sortedBySentiment}
        sortedByGrowth={sortedByGrowth}
        sortedByTraffic={sortedByTrafficForModal}
        sortedBySeo={sortedBySeo}
        demoMode={demoMode}
        demoTrafficHistory={demoMode ? demoData?.modalTrafficHistory : undefined}
        demoPricingData={demoMode ? demoData?.buildDemoPricing : undefined}
      />
    </Box>
  );
};

export default WebTrafficDashboard; 